/**
 * Vimeo Embed Component
 * Story 8.2: Home Dashboard - Shared video embed component
 *
 * Reusable Vimeo video embed with placeholder state.
 * Used by Home Dashboard tutorials and Onboarding (Raila's greeting).
 *
 * @module components/video/VimeoEmbed
 */

import { useTranslation } from '../../locales';

export interface VimeoEmbedProps {
  /** Vimeo video ID (null shows placeholder) */
  vimeoId: string | null;
  /** Video title for accessibility */
  title: string;
  /** Optional custom aspect ratio class */
  aspectRatio?: string;
  /** Auto-start video when loaded (default: false) */
  autoplay?: boolean;
  /** Start muted - required for autoplay to work in browsers (default: false) */
  muted?: boolean;
}

/**
 * Vimeo video embed with placeholder support
 *
 * When vimeoId is null, shows a placeholder message.
 * When vimeoId is provided, shows the Vimeo iframe embed.
 */
export function VimeoEmbed({
  vimeoId,
  title,
  aspectRatio = 'aspect-video',
  autoplay = false,
  muted = false,
}: VimeoEmbedProps): JSX.Element {
  const { t } = useTranslation();

  // Placeholder state when no video ID
  if (!vimeoId) {
    return (
      <div
        className={`${aspectRatio} w-full flex items-center justify-center bg-gray-100 rounded-lg`}
      >
        <p className="text-gray-500 text-center px-4">{t('home.tutorials.placeholder')}</p>
      </div>
    );
  }

  // Build Vimeo URL with optional autoplay and muted parameters
  const autoplayParam = autoplay ? '1' : '0';
  const mutedParam = muted ? '1' : '0';
  const vimeoUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=${autoplayParam}&muted=${mutedParam}&title=0&byline=0&portrait=0`;

  // Vimeo iframe embed
  return (
    <div className={`${aspectRatio} w-full`}>
      <iframe
        src={vimeoUrl}
        className="w-full h-full rounded-lg"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        title={title}
      />
    </div>
  );
}
